import React, { ChangeEvent, FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, TextField } from '@mui/material';
import { useQuery } from '@apollo/client';
import { LabOrderContext } from 'pages/order-flow/lab-order/lab-order-context';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { panNumbersTemplate, panNumberFeatureStatusTemplate } from 'apollo/templates/pan-number';
import { PanNumber } from 'models/pan-number';
import PanNumberSelect from 'components/shared/pan-number-select/pan-number-select.component';

type InternalSectionProps = {
    onPanNumberEnabledChange: (panNumberEnabled: boolean) => void;
};

const InternalSection: FC<InternalSectionProps> = ({ onPanNumberEnabledChange }) => {
    const { t: translate } = useTranslation(['orders']);
    const { order, setOrder } = useContext(LabOrderContext);
    const [panNumbers, setPanNumbers] = useState<PanNumber[]>([]);
    const [panNumberEnabled, setPanNumberEnabled] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    useQuery(panNumbersTemplate, {
        onCompleted: ({ panNumbers: fetchedPanNumbers }) => {
            setPanNumbers(fetchedPanNumbers);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useQuery(panNumberFeatureStatusTemplate, {
        onCompleted: ({ currentLab }) => {
            const enabled = currentLab?.panNumberEnabled ?? false;
            setPanNumberEnabled(enabled);
            onPanNumberEnabledChange(enabled);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleInternalOrderIdChange = (event: ChangeEvent<HTMLInputElement>) => {
        setOrder((prev) => ({
            ...prev,
            internalOrderId: event.target.value,
        }));
    };

    const handlePanNumberChange = ({ value, description }: PanNumber) => {
        setOrder((prev) => ({ ...prev, panNumber: value, panNumberDescription: description }));
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
                <TextField
                    fullWidth
                    label={translate('orders:INTERNAL_ORDER_ID')}
                    value={order.internalOrderId || ''}
                    onChange={handleInternalOrderIdChange}
                    data-test="patient-lab-step.internal-order-id-input"
                />
            </Grid>
            {panNumberEnabled && (
                <Grid item xs={12} md={4}>
                    <PanNumberSelect
                        panNumbers={panNumbers}
                        selectedPanNumber={order.panNumber}
                        onPanNumberChange={handlePanNumberChange}
                    />
                </Grid>
            )}
        </Grid>
    );
};

export default InternalSection;
